import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { coursesService, imageService } from '../services/api';
import { format } from 'date-fns';
import PriceBreakdownButton from './PriceBreakdownButton';
import { useIsMobile } from '../hooks';
import { BookOpen as BookOpenIcon, Image as ImageIcon, Lock as LockIcon, Pencil as PencilIcon, Plus as PlusIcon, Trash2 as Trash2Icon } from 'lucide-react';

// Colors and Icons (matching existing style)
const Colors = {
  primary: '#1A3E2A',
  secondary: '#5F6B61',
  accent: '#1A3E2A',
  warning: '#B68235',
  danger: '#A13F2F',
  success: '#163626',
  white: '#ffffff',
  light: '#F5F3EB',
  border: '#E3DFD3',
  muted: '#A5A89B',
  available: '#1A3E2A',
  occupied: '#A13F2F'
};

const Icons = {
  add: '',
  edit: '',
  delete: '',
  image: '',
  course: '',
  close: '✕',
  upload: '',
  save: ''
};

function CourseManagement({ user, onDataUpdate }) {
  const isMobile = useIsMobile();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fee: 0,
    courseType: 'fixed', // 'fixed' or 'duration'
    startDate: '',
    endDate: '',
    duration: null
  });
  const [formErrors, setFormErrors] = useState({});

  // Image management states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseImages, setCourseImages] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imageDescription, setImageDescription] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  const canManageCourses = ['STAFF', 'PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role);
  const canCreateCourses = ['STAFF', 'PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role);
  const canDeleteCourses = ['PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role);

  useEffect(() => {
    if (canManageCourses) {
      loadCourses();
    }
  }, [canManageCourses]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCourses = async () => {
    try {
      setLoading(true);
      // Walks every page of /api/courses so the full catalogue is rendered,
      // not just the first page returned by the paginated endpoint
      const response = await coursesService.getAllPages({
        sortBy: 'startDate',
        sortOrder: 'desc'
      });
      const coursesData = response.courses || [];
      const sortedCourses = sortCoursesForDisplay(coursesData);
      setCourses(sortedCourses);

      // Load images for all courses in the background so the table renders
      // immediately instead of waiting on one request per course
      loadAllCourseImages(sortedCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Failed to load courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllCourseImages = async (coursesData) => {
    try {
      // Fetched in batches so a large catalogue doesn't fire one request per
      // course all at once
      const BATCH_SIZE = 10;
      const imageMap = {};

      for (let i = 0; i < coursesData.length; i += BATCH_SIZE) {
        const batch = coursesData.slice(i, i + BATCH_SIZE);
        const imageResults = await Promise.all(
          batch.map(async (course) => {
            try {
              const imagesResponse = await imageService.getImages('course', course.id);
              return { courseId: course.id, images: imagesResponse.data || [] };
            } catch (error) {
              return { courseId: course.id, images: [] };
            }
          })
        );

        imageResults.forEach(({ courseId, images }) => {
          imageMap[courseId] = images;
        });
      }

      setCourseImages(imageMap);
    } catch (error) {
      console.error('Error loading course images:', error);
    }
  };

  const sortCoursesForDisplay = (coursesData) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isPastCourse = (course) => {
      if (!course.endDate) return false;
      const endDate = new Date(course.endDate);
      endDate.setHours(0, 0, 0, 0);
      return endDate < today;
    };

    const getSortDate = (course) => {
      if (course.startDate) return new Date(course.startDate);
      if (course.endDate) return new Date(course.endDate);
      return new Date(0);
    };

    return [...coursesData].sort((a, b) => {
      const aPast = isPastCourse(a);
      const bPast = isPastCourse(b);
      if (aPast !== bPast) {
        return aPast ? 1 : -1;
      }

      const aDate = getSortDate(a);
      const bDate = getSortDate(b);
      return bDate - aDate;
    });
  };

  const loadCourseImages = async (courseId) => {
    try {
      const imagesResponse = await imageService.getImages('course', courseId);
      setCourseImages(prev => ({
        ...prev,
        [courseId]: imagesResponse.data || []
      }));
    } catch (error) {
      console.error('Error loading course images:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      fee: 0,
      courseType: 'fixed',
      startDate: '',
      endDate: '',
      duration: null
    });
    setFormErrors({});
    setEditingCourse(null);
    setShowAddForm(false);
  };

  // Image Management Functions
  const openImageModal = (course) => {
    setSelectedCourse(course);
    setSelectedFiles([]);
    setImageDescription('');
    setImagePreview(null);
    setShowImageModal(true);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    
    // Create preview for single image
    if (files.length === 1) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(files[0]);
    } else {
      setImagePreview(null);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedCourse || selectedFiles.length === 0) {
      toast.error('Please select a course and at least one image');
      return;
    }

    setUploadingImages(true);
    try {
      const formData = new FormData();
      
      if (selectedFiles.length === 1) {
        // Single image upload
        formData.append('image', selectedFiles[0]);
      } else {
        // Multiple image upload
        selectedFiles.forEach(file => {
          formData.append('images', file);
        });
      }
      
      formData.append('courseId', selectedCourse.id);
      if (imageDescription.trim()) {
        formData.append('description', imageDescription.trim());
      }

      if (selectedFiles.length === 1) {
        await imageService.uploadSingle(formData);
        toast.success('Image uploaded successfully');
      } else {
        await imageService.uploadMultiple(formData);
        toast.success(`${selectedFiles.length} images uploaded successfully`);
      }

      // Reload images for this course
      await loadCourseImages(selectedCourse.id);
      
      // Reset form
      setSelectedFiles([]);
      setImageDescription('');
      setImagePreview(null);
      setShowImageModal(false);
      
    } catch (error) {
      toast.error('Failed to upload images: ' + (error.message || 'Unknown error'));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId, courseId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await imageService.deleteImage(imageId);
      toast.success('Image deleted successfully');
      await loadCourseImages(courseId);
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Course name is required';
    }

    if (formData.courseType === 'fixed') {
      if (!formData.startDate) {
        errors.startDate = 'Start date is required for fixed date courses';
      }

      if (!formData.endDate) {
        errors.endDate = 'End date is required for fixed date courses';
      }

      if (formData.startDate && formData.endDate) {
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        if (endDate <= startDate) {
          errors.endDate = 'End date must be after start date';
        }
      }
    } else if (formData.courseType === 'duration') {
      if (!formData.duration || formData.duration <= 0) {
        errors.duration = 'Duration must be a positive number of days';
      }
    }

    if (formData.fee < 0) {
      errors.fee = 'Course fee cannot be negative';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Toggle between course types
  const handleCourseTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      courseType: type,
      // Clear opposite type fields
      ...(type === 'fixed' ? { duration: null } : { startDate: '', endDate: '' })
    }));
    // Clear related errors
    setFormErrors(prev => {
      const newErrors = {...prev};
      if (type === 'fixed') {
        delete newErrors.duration;
      } else {
        delete newErrors.startDate;
        delete newErrors.endDate;
      }
      return newErrors;
    });
  };

  // Prepare data for API submission
  const prepareSubmissionData = () => {
    const baseData = {
      name: formData.name,
      description: formData.description,
      fee: parseFloat(formData.fee) || 0
    };
    
    if (formData.courseType === 'fixed') {
      return {
        ...baseData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString()
      };
    } else {
      return {
        ...baseData,
        duration: parseInt(formData.duration)
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    try {
      setLoading(true);
      
      const courseData = prepareSubmissionData();

      if (editingCourse) {
        await coursesService.update(editingCourse.id, courseData);
        toast.success('Course updated successfully');
      } else {
        await coursesService.create(courseData);
        toast.success('Course created successfully');
      }

      resetForm();
      loadCourses();
      if (onDataUpdate) onDataUpdate();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(editingCourse ? 'Failed to update course' : 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    
    // Determine course type based on available data
    const isFixedDate = course.startDate && course.endDate;
    
    setFormData({
      name: course.name || '',
      description: course.description || '',
      fee: course.fee || 0,
      courseType: isFixedDate ? 'fixed' : 'duration',
      startDate: course.startDate ? new Date(course.startDate).toISOString().split('T')[0] : '',
      endDate: course.endDate ? new Date(course.endDate).toISOString().split('T')[0] : '',
      duration: course.duration || null
    });
    setShowAddForm(true);
  };

  const handleDelete = async (course) => {
    if (!canDeleteCourses) {
      toast.error('You do not have permission to delete courses');
      return;
    }

    const confirmMessage = `Are you sure you want to delete "${course.name}"?\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      await coursesService.delete(course.id);
      toast.success('Course deleted successfully');
      loadCourses();
      if (onDataUpdate) onDataUpdate();
    } catch (error) {
      console.error('Error deleting course:', error);
      if (error.message?.includes('existing bookings')) {
        toast.error('Cannot delete course with existing bookings');
      } else {
        toast.error('Failed to delete course');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const calculateDuration = (startDate, endDate) => {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return 0;
    }
  };

  const getStatusBadge = (course) => {
    // For duration-based courses, they're always available
    if (course.duration && !course.startDate && !course.endDate) {
      return <span className="badge" style={{ backgroundColor: '#1A3E2A', color: 'white' }}>Available Year-Round</span>;
    }
    
    // For fixed date courses
    if (course.startDate && course.endDate) {
      const now = new Date();
      const startDate = new Date(course.startDate);
      const endDate = new Date(course.endDate);

      if (now < startDate) {
        return <span className="badge" style={{ backgroundColor: '#1A3E2A', color: 'white' }}>Upcoming</span>;
      } else if (now >= startDate && now <= endDate) {
        return <span className="badge" style={{ backgroundColor: '#1A3E2A', color: 'white' }}>Active</span>;
      } else {
        return <span className="badge" style={{ backgroundColor: '#5F6B61', color: 'white' }}>Completed</span>;
      }
    }
    
    return <span className="badge" style={{ backgroundColor: '#B68235', color: 'white' }}>Unknown</span>;
  };

  if (!canManageCourses) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}><LockIcon size="1em" aria-hidden="true" /></div>
        <h3>Access Denied</h3>
        <p>You don't have permission to manage courses.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: isMobile ? '22px' : '24px' }}>Course Management</h3>
          <p style={{ color: '#5F6B61', margin: '5px 0 0 0', fontSize: '14px' }}>
            Manage fixed-date retreats and year-round duration-based courses
          </p>
        </div>
        {canCreateCourses && (
          <button
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
          >
            <span><PlusIcon size="1em" aria-hidden="true" /></span>
            Add Course
          </button>
        )}
      </div>

      {/* Add/Edit Course Form */}
      {showAddForm && (
        <div style={{ 
          backgroundColor: '#F5F3EB', 
          padding: isMobile ? '14px' : '24px', 
          borderRadius: '12px', 
          marginBottom: '24px',
          border: '1px solid #E3DFD3'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '20px' }}>
            {editingCourse ? 'Edit Course' : 'Add New Course'}
          </h4>
          
          <form onSubmit={handleSubmit}>
            {/* Course Type Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#36433B' }}>
                Course Type *
              </label>
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                flexDirection: isMobile ? 'column' : 'row',
                padding: '16px',
                backgroundColor: '#F5F3EB',
                borderRadius: '8px',
                border: '1px solid #E3DFD3'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: formData.courseType === 'fixed' ? '#1A3E2A' : 'transparent',
                  color: formData.courseType === 'fixed' ? 'white' : '#36433B',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="radio"
                    name="courseType"
                    value="fixed"
                    checked={formData.courseType === 'fixed'}
                    onChange={(e) => handleCourseTypeChange(e.target.value)}
                    style={{ margin: 0 }}
                  />
                  <span style={{ fontWeight: '500' }}>Fixed Date Course</span>
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: formData.courseType === 'duration' ? '#1A3E2A' : 'transparent',
                  color: formData.courseType === 'duration' ? 'white' : '#36433B',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="radio"
                    name="courseType"
                    value="duration"
                    checked={formData.courseType === 'duration'}
                    onChange={(e) => handleCourseTypeChange(e.target.value)}
                    style={{ margin: 0 }}
                  />
                  <span style={{ fontWeight: '500' }}>Duration-Based Course</span>
                </label>
              </div>
              <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '8px' }}>
                {formData.courseType === 'fixed' ? 
                  "Course with specific start and end dates" : 
                  "Open course available year-round with a fixed duration"
                }
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#36433B' }}>
                  Course Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter course name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: formErrors.name ? '2px solid #A13F2F' : '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {formErrors.name && (
                  <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {formErrors.name}
                  </span>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#36433B' }}>
                  Course Fee ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.fee}
                  onChange={(e) => setFormData({...formData, fee: e.target.value})}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: formErrors.fee ? '2px solid #A13F2F' : '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {formErrors.fee && (
                  <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {formErrors.fee}
                  </span>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#36433B' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Enter course description"
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Conditional Fields based on Course Type */}
            {formData.courseType === 'fixed' ? (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#36433B' }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: formErrors.startDate ? '2px solid #A13F2F' : '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {formErrors.startDate && (
                    <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {formErrors.startDate}
                    </span>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#36433B' }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: formErrors.endDate ? '2px solid #A13F2F' : '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {formErrors.endDate && (
                    <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {formErrors.endDate}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#36433B' }}>
                  Course Duration (Days) *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.duration || ''}
                  onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value) || null})}
                  placeholder="Enter duration in days (e.g., 7, 14, 21)"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: formErrors.duration ? '2px solid #A13F2F' : '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {formErrors.duration && (
                  <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {formErrors.duration}
                  </span>
                )}
                <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '4px' }}>
                  Guests can book this course at any time throughout the year
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
                style={{ opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Saving...' : (editingCourse ? 'Update Course' : 'Create Course')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Courses List */}
      {loading && !showAddForm ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Loading courses...</div>
        </div>
      ) : courses.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: '#F5F3EB',
          borderRadius: '12px',
          border: '1px solid #E3DFD3'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}><BookOpenIcon size="1em" aria-hidden="true" /></div>
          <h4 style={{ color: '#36433B', marginBottom: '8px' }}>No Courses Found</h4>
          <p style={{ color: '#5F6B61', marginBottom: '20px' }}>
            Create fixed-date courses or year-round duration-based programs.
          </p>
          {canCreateCourses && (
            <button
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              Create First Course
            </button>
          )}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: isMobile ? '900px' : '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ backgroundColor: '#F5F3EB', borderBottom: '2px solid #E3DFD3' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#36433B' }}>Course Details</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#36433B' }}>Schedule</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#36433B' }}>Pricing</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#36433B' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#36433B' }}>Bookings</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#36433B' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map(course => (
                <tr key={course.id} style={{ borderBottom: '1px solid #E3DFD3' }}>
                  <td style={{ padding: '12px' }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#24332A', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {course.name}
                        {courseImages[course.id] && courseImages[course.id].length > 0 && (
                          <span style={{
                            fontSize: '10px',
                            backgroundColor: Colors.accent,
                            color: Colors.white,
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontWeight: '500'
                          }}>
                            {courseImages[course.id].length} {courseImages[course.id].length === 1 ? 'image' : 'images'}
                          </span>
                        )}
                      </div>
                      {course.description && (
                        <div style={{ fontSize: '12px', color: '#5F6B61', lineHeight: '1.4' }}>
                          {course.description.length > 100 
                            ? course.description.substring(0, 100) + '...' 
                            : course.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '13px' }}>
                      {course.startDate && course.endDate ? (
                        <>
                          <div><strong>Start:</strong> {formatDate(course.startDate)}</div>
                          <div><strong>End:</strong> {formatDate(course.endDate)}</div>
                          <div style={{ color: '#5F6B61', marginTop: '2px' }}>
                            Duration: {calculateDuration(course.startDate, course.endDate)} days
                          </div>
                        </>
                      ) : course.duration ? (
                        <>
                          <div style={{ color: '#1A3E2A', fontWeight: '600' }}>
                            Duration: {course.duration} days
                          </div>
                          <div style={{ color: '#5F6B61', marginTop: '2px', fontSize: '12px' }}>
                            Available year-round
                          </div>
                        </>
                      ) : (
                        <div style={{ color: '#A13F2F', fontSize: '12px' }}>
                          Invalid course configuration
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: course.fee > 0 ? '#163626' : '#5F6B61' }}>
                      ${course.fee || 0}
                    </div>
                    <div style={{ fontSize: '11px', color: '#5F6B61' }}>
                      {course.fee > 0 ? 'Course Fee' : 'Free Course'}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {getStatusBadge(course)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '13px' }}>
                      {course.bookings ? (
                        <>
                          <div><strong>{course.bookings.filter(b => b.status === 'CONFIRMED').length}</strong> confirmed</div>
                          <div style={{ color: '#5F6B61' }}>
                            {course.bookings.filter(b => b.status === 'PENDING').length} pending
                          </div>
                        </>
                      ) : (
                        <span style={{ color: '#5F6B61' }}>No bookings</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <PriceBreakdownButton
                        entityType="course"
                        entityId={course.id}
                        entity={{ name: course.name, basePrice: course.fee }}
                        onUpdate={loadCourses}
                        variant="icon"
                        size="small"
                      />
                      <button
                        onClick={() => openImageModal(course)}
                        className="btn btn-sm"
                        style={{ 
                          backgroundColor: '#1A3E2A', 
                          color: 'white',
                          fontSize: '12px',
                          padding: '4px 8px'
                        }}
                        title="Manage course images"
                      >
                        <ImageIcon size="1em" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleEdit(course)}
                        className="btn btn-sm"
                        style={{ 
                          backgroundColor: '#1A3E2A', 
                          color: 'white',
                          fontSize: '12px',
                          padding: '4px 8px'
                        }}
                        title="Edit course"
                      >
                        <PencilIcon size="1em" aria-hidden="true" />
                      </button>
                      {canDeleteCourses && (
                        <button
                          onClick={() => handleDelete(course)}
                          className="btn btn-sm"
                          style={{ 
                            backgroundColor: '#A13F2F', 
                            color: 'white',
                            fontSize: '12px',
                            padding: '4px 8px'
                          }}
                          title="Delete course"
                        >
                          <Trash2Icon size="1em" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Image Management Modal */}
      {showImageModal && selectedCourse && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '12px',
            width: '700px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ padding: '24px' }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '24px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.image}</span>
                Manage Images - {selectedCourse.name}
              </h3>

              {/* Current Images */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: Colors.secondary
                }}>
                  Current Images ({courseImages[selectedCourse.id]?.length || 0})
                </h4>
                
                {courseImages[selectedCourse.id] && courseImages[selectedCourse.id].length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '12px'
                  }}>
                    {courseImages[selectedCourse.id].map((image) => (
                      <div
                        key={image.id}
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: Colors.light,
                          border: `1px solid ${Colors.border}`
                        }}
                      >
                        <img
                          src={image.url}
                          alt={image.description || selectedCourse.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <button
                          onClick={() => handleDeleteImage(image.id, selectedCourse.id)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: 'rgba(161, 63, 47, 0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 6px',
                            cursor: 'pointer',
                            color: Colors.white,
                            fontSize: '12px'
                          }}
                        >
                          {Icons.close}
                        </button>
                        {image.description && (
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: Colors.white,
                            padding: '4px 6px',
                            fontSize: '11px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          }}>
                            {image.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '24px',
                    color: Colors.muted,
                    fontSize: '14px',
                    backgroundColor: Colors.light,
                    borderRadius: '8px',
                    border: `1px dashed ${Colors.border}`
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>{Icons.image}</div>
                    No images uploaded yet
                  </div>
                )}
              </div>

              {/* Upload New Images */}
              <div style={{
                border: `2px dashed ${Colors.border}`,
                borderRadius: '8px',
                padding: '24px',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="course-image-upload"
                />
                <label
                  htmlFor="course-image-upload"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: Colors.primary,
                    color: Colors.white,
                    marginBottom: '12px'
                  }}
                >
                  <span>{Icons.upload}</span>
                  Select Images
                </label>
                
                <p style={{
                  margin: '8px 0 0 0',
                  color: Colors.muted,
                  fontSize: '12px'
                }}>
                  Select one or multiple images (JPEG, PNG, WebP)
                </p>

                {selectedFiles.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: Colors.secondary
                    }}>
                      Selected Files: {selectedFiles.length}
                    </p>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      justifyContent: 'center'
                    }}>
                      {Array.from(selectedFiles).map((file, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: Colors.light,
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: Colors.secondary
                          }}
                        >
                          {file.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {imagePreview && (
                  <div style={{
                    marginTop: '16px',
                    position: 'relative',
                    display: 'inline-block'
                  }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        border: `1px solid ${Colors.border}`
                      }}
                    />
                    <button
                      onClick={() => {
                        setSelectedFiles([]);
                        setImagePreview(null);
                      }}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(161, 63, 47, 0.9)',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        color: Colors.white,
                        fontSize: '12px'
                      }}
                    >
                      {Icons.close}
                    </button>
                  </div>
                )}
              </div>

              {/* Image Description */}
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Image Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={imageDescription}
                    onChange={(e) => setImageDescription(e.target.value)}
                    placeholder="Describe the images..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              {/* Modal Actions */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    setShowImageModal(false);
                    setSelectedCourse(null);
                    setSelectedFiles([]);
                    setImageDescription('');
                    setImagePreview(null);
                  }}
                  style={{
                    padding: '12px 20px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: Colors.white,
                    color: Colors.secondary
                  }}
                >
                  Cancel
                </button>
                
                {selectedFiles.length > 0 && (
                  <button
                    onClick={handleImageUpload}
                    disabled={uploadingImages}
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      backgroundColor: Colors.primary,
                      color: Colors.white,
                      opacity: uploadingImages ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>{Icons.upload}</span>
                    {uploadingImages ? 'Uploading...' : 'Upload Images'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseManagement;